import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useApp } from "../context/AppContext";
import { ANALYTICS_DATA } from "../data/mockData";
import StatCard from "../components/StatCard";
import Badge from "../components/ui/Badge";
import {
  Package,
  Eye,
  ShoppingBag,
  TrendingUp,
  Star,
  MapPin,
  ArrowRight,
  Flame,
  BarChart2,
  DollarSign,
} from "lucide-react";

const fmt = (n) =>
  n >= 1_000_000
    ? `₦${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000
      ? `₦${(n / 1_000).toFixed(0)}K`
      : `₦${n}`;

const fmtNum = (n) => (n >= 1_000 ? `${(n / 1_000).toFixed(1)}K` : String(n));

// Mini sparkline bar chart
function MiniBar({ data, color = "#8b5cf6" }) {
  const max = Math.max(...data);
  return (
    <div className="flex items-end gap-0.5 h-10">
      {data.map((v, i) => (
        <div
          key={i}
          className="flex-1 rounded-sm transition-all"
          style={{
            height: `${(v / max) * 100}%`,
            background: i === data.length - 1 ? color : `${color}55`,
            minHeight: "3px",
          }}
        />
      ))}
    </div>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const { getProducts } = useApp();
  const navigate = useNavigate();

  const products = getProducts(user.id);
  const analytics = ANALYTICS_DATA[user.id] || ANALYTICS_DATA[1];

  const totalViews = products.reduce((s, p) => s + (p.views || 0), 0);
  const totalSales = products.reduce((s, p) => s + (p.sales || 0), 0);
  const activeProducts = products.filter((p) => p.status === "active").length;
  const featured = products.filter((p) => p.featured).length;

  const topProducts = [...products]
    .sort((a, b) => b.sales - a.sales)
    .slice(0, 4);

  const canSeeRevenue = user.plan !== "Free";

  return (
    <div className="space-y-6 max-w-7xl mx-auto">
      {/* Welcome banner */}
      <div className="bg-gradient-to-r from-violet-600/20 via-indigo-600/10 to-transparent border border-violet-600/20 rounded-2xl p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center text-base font-bold text-white flex-shrink-0">
          {user.initials}
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-base font-bold text-gray-900 dark:text-gray-100">
            Good day, {user.name.split(" ")[0]}! 👋
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            {user.business} · <MapPin size={12} className="inline" />{" "}
            {user.marketCity || user.location || "—"}
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Badge variant={user.plan}>{user.plan} Plan</Badge>
          {user.verified && (
            <span className="flex items-center gap-1 text-xs text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-2.5 py-1 rounded-full">
              <Star size={11} fill="currentColor" /> Verified
            </span>
          )}
        </div>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Total Products"
          value={products.length}
          sub={`${activeProducts} active`}
          icon={Package}
          color="violet"
          trend="up"
          trendValue="+3"
        />
        <StatCard
          title="Total Sales"
          value={fmtNum(totalSales)}
          sub="All time"
          icon={ShoppingBag}
          color="emerald"
          trend="up"
          trendValue="+12%"
        />
        <StatCard
          title="Product Views"
          value={fmtNum(totalViews)}
          sub="Last 30 days"
          icon={Eye}
          color="blue"
          trend="up"
          trendValue="+8%"
        />
        <StatCard
          title={canSeeRevenue ? "Total Revenue" : "Featured"}
          value={canSeeRevenue ? fmt(analytics.totalRevenue) : featured}
          sub={canSeeRevenue ? "Estimated" : "Featured listings"}
          icon={canSeeRevenue ? DollarSign : Flame}
          color={canSeeRevenue ? "amber" : "amber"}
          trend="up"
          trendValue="+5%"
        />
      </div>

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Sales trend */}
        <div className="lg:col-span-2 card p-5">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                Sales Trend
              </h3>
              <p className="text-xs text-gray-500 dark:text-gray-500 mt-0.5">
                Monthly performance this year
              </p>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-emerald-400 bg-emerald-500/10 px-2.5 py-1 rounded-full border border-emerald-500/20">
              <TrendingUp size={12} />+
              {Math.round(
                ((analytics.salesTrend[11] - analytics.salesTrend[0]) /
                  analytics.salesTrend[0]) *
                  100,
              )}
              % YTD
            </div>
          </div>

          {canSeeRevenue ? (
            <div className="flex items-end gap-1.5 h-28">
              {analytics.salesTrend.map((v, i) => {
                const max = Math.max(...analytics.salesTrend);
                return (
                  <div
                    key={i}
                    className="flex-1 flex flex-col items-center gap-1.5">
                    <div
                      className="w-full rounded-md transition-all duration-300 hover:opacity-100"
                      style={{
                        height: `${(v / max) * 100}px`,
                        background:
                          i === analytics.salesTrend.length - 1
                            ? "linear-gradient(to top, #7c3aed, #6366f1)"
                            : "rgba(139,92,246,0.3)",
                        minHeight: "4px",
                      }}
                    />
                    <span className="text-[9px] text-gray-500 dark:text-gray-600">
                      {analytics.months[i]}
                    </span>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="h-28 flex flex-col items-center justify-center border border-dashed border-gray-300 dark:border-gray-700 rounded-xl">
              <BarChart2
                size={28}
                className="text-gray-400 dark:text-gray-700 mb-2"
              />
              <p className="text-sm text-gray-500 dark:text-gray-600">
                Advanced analytics unlocked
              </p>
              <button
                onClick={() => navigate("/dashboard/pricing")}
                className="text-xs text-violet-400 hover:text-violet-300 mt-1.5 underline underline-offset-2">
                Upgrade to Advanced →
              </button>
            </div>
          )}
        </div>

        {/* Quick stats */}
        <div className="card p-5 flex flex-col gap-4">
          <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
            At a Glance
          </h3>
          {[
            {
              label: "Conversion Rate",
              value: `${analytics.conversionRate}%`,
              icon: TrendingUp,
              color: "text-emerald-400",
            },
            {
              label: "Total Orders",
              value: fmtNum(analytics.totalOrders),
              icon: ShoppingBag,
              color: "text-blue-400",
            },
            {
              label: "Total Views",
              value: fmtNum(analytics.totalViews),
              icon: Eye,
              color: "text-violet-400",
            },
          ].map(({ label, value, icon: Icon, color }) => (
            <div key={label} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex items-center justify-center flex-shrink-0">
                <Icon size={15} className={color} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs text-gray-500">{label}</p>
                <p className="text-base font-bold text-gray-900 dark:text-gray-100">
                  {value}
                </p>
              </div>
              <div className="w-16">
                <MiniBar
                  data={analytics.salesTrend.slice(-6)}
                  color={
                    color.includes("emerald")
                      ? "#10b981"
                      : color.includes("blue")
                        ? "#3b82f6"
                        : "#8b5cf6"
                  }
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Top products */}
      <div className="card p-5">
        <div className="flex items-center justify-between mb-5">
          <div>
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
              Top Products
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Best performing by sales
            </p>
          </div>
          <button
            onClick={() => navigate("/dashboard/products")}
            className="flex items-center gap-1.5 text-xs text-violet-400 hover:text-violet-300 transition-colors font-medium">
            View all <ArrowRight size={13} />
          </button>
        </div>
        <div className="space-y-3">
          {topProducts.length === 0 && (
            <p className="text-sm text-gray-400 dark:text-gray-600 py-4 text-center">
              No products yet. Add some!
            </p>
          )}
          {topProducts.map((p, idx) => (
            <div
              key={p.id}
              className="flex items-center gap-4 p-3 rounded-xl bg-gray-100/60 dark:bg-gray-800/40 hover:bg-gray-100 dark:hover:bg-gray-800/70 transition-colors cursor-pointer"
              onClick={() => navigate("/dashboard/products")}>
              <span className="text-xs font-semibold text-gray-400 dark:text-gray-600 w-5 text-center flex-shrink-0">
                #{idx + 1}
              </span>
              <div className="w-9 h-9 rounded-xl bg-gray-200 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 flex items-center justify-center flex-shrink-0">
                <Package
                  size={16}
                  className="text-gray-400 dark:text-gray-500"
                />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">
                  {p.name}
                </p>
                <p className="text-xs text-gray-500">{p.category}</p>
              </div>
              <div className="text-right flex-shrink-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  ₦{p.price.toLocaleString()}
                </p>
                <p className="text-xs text-gray-500">{p.sales} sold</p>
              </div>
              {p.featured && (
                <span className="text-[10px] font-semibold text-amber-400 bg-amber-500/10 border border-amber-500/20 px-2 py-0.5 rounded-full flex-shrink-0">
                  Featured
                </span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
